import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TerminalViewer from '../../shared/terminal/TerminalViewer';
import { getAccessToken } from '../../../auth';
import { envConfig } from '../../../config';
import { EditorService } from '../../../services/editor';
import { InfrastructureResourceType } from '@elyx-code/definitions';
import {
	BUILT_IN_BASE_ENTITY_IDS,
	PropertyState,
	resolveActionDescriptorNameById,
	toSentenceCase,
	toKebabCase
} from '@elyx-code/project-logic-tree';
import Button from '../../../lib/button/Button';
import CloudLogsViewer from '../logs/CloudLogsViewer';
import ContainerComputeIcon from '../../../assets/container-compute.svg';
import SharedDriveIcon from '../../../assets/shared-drive.svg';

const CopyableIp = ({ label, ip }: { label: string; ip: string }) => {
	const [hovered, setHovered] = useState(false);
	const [copied, setCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(ip);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<span
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			style={{
				fontSize: '10px',
				color: '#888',
				display: 'flex',
				alignItems: 'center',
				gap: '4px'
			}}
		>
			{label}: <span style={{ fontFamily: 'monospace' }}>{ip}</span>
			<div
				onClick={handleCopy}
				style={{
					opacity: hovered ? 1 : 0,
					cursor: 'pointer',
					transition: 'opacity 0.2s',
					display: 'flex',
					alignItems: 'center',
					padding: '2px',
					borderRadius: '4px',
					backgroundColor: hovered
						? 'rgba(0,0,0,0.05)'
						: 'transparent'
				}}
				title="Copy IP"
			>
				{copied ? (
					<svg
						width="12"
						height="12"
						viewBox="0 0 24 24"
						fill="none"
						stroke="#10b981"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="20 6 9 17 4 12"></polyline>
					</svg>
				) : (
					<svg
						width="12"
						height="12"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<rect
							x="9"
							y="9"
							width="13"
							height="13"
							rx="2"
							ry="2"
						></rect>
						<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
					</svg>
				)}
			</div>
		</span>
	);
};

interface IContainerComputeViewerProps {
	onClose: () => void;
	project: EditorService;
	projectId: string;
}

const ContainerComputeViewer: React.FC<IContainerComputeViewerProps> = ({
	onClose,
	project,
	projectId
}) => {
	const params = useParams<{ computeId: string }>();
	const navigate = useNavigate();
	const computeId = params.computeId || '';
	const [tasks, setTasks] = useState<
		{
			privateIp: string;
			publicIp: string;
			status: 'RUNNING' | 'STOPPED' | 'PROVISIONING' | 'PENDING';
			taskArn: string;
			stoppedReason?: string;
		}[]
	>([]);
	const [selectedTask, setSelectedTask] = useState<any | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState<'terminal' | 'logs'>('terminal');

	const headerInfo = React.useMemo(() => {
		const resources = (project?.liveResources as any[]) || [];
		const computeResources = resources.filter(
			(r) =>
				r.resourceType ===
					(InfrastructureResourceType as any).Compute ||
				r.resourceType === 'CONTAINER_COMPUTE' ||
				r.resourceType === 'VOLUME'
		);
		const matchingRes = computeResources.find(
			(r) => r.resourceId === computeId
		);

		const matchEntity = (
			project?.lastPublishedProjectState?.entities || []
		).find((e: any) => computeId === e.id);

		const resolvedName = matchEntity
			? matchEntity.name ||
				resolveActionDescriptorNameById(matchEntity.id)
			: null;

		if (matchingRes || matchEntity) {
			const rawName = matchingRes?.name || computeId;
			const readableName =
				resolvedName ||
				resolveActionDescriptorNameById(rawName) ||
				toSentenceCase(rawName) ||
				'Compute Instance';
			return {
				name: readableName,
				icon: 'fa-solid fa-server'
			};
		}

		return {
			name: computeId || 'Terminal',
			icon: 'fa-solid fa-server'
		};
	}, [project?.liveResources, computeId]);

	const tenantAccountId = React.useMemo(() => {
		const resources = (project?.liveResources as any[]) || [];
		const accountResource = resources.find(
			(r) => r.resourceType === 'aws-tenant-account'
		);
		return accountResource?.details?.awsAccountId || null;
	}, [project?.liveResources]);

	const envIdentifier = React.useMemo(() => {
		const resources = (project?.liveResources as any[]) || [];
		const computeRes = resources.find((r) => r.resourceId === computeId);
		if (computeRes?.details?.clusterName) {
			const arnParts = computeRes.details.clusterName.split('/');
			if (arnParts.length > 1) {
				const clusterNameStr = arnParts[arnParts.length - 1];
				return clusterNameStr.replace('elyx-cluster-', '');
			}
		}
		return 'main';
	}, [project?.liveResources, computeId]);

	const containerDetails = React.useMemo(() => {
		const resources = (project?.liveResources as any[]) || [];
		const computeRes = resources.find((r) => r.resourceId === computeId);
		return computeRes?.details || null;
	}, [project?.liveResources, computeId]);

	const containerConfig = React.useMemo(() => {
		const matchEntity = (
			project?.lastPublishedProjectState?.entities || []
		).find((e: any) => computeId === e.id);
		const properties: PropertyState[] = matchEntity?.properties || [];

		const maxTasksProperty = properties.find((property) =>
			property.implements.find(
				(base) =>
					base.id ===
					BUILT_IN_BASE_ENTITY_IDS['built-in-base-entity']
						.BUILT_IN_CONTAINER_COMPUTE_ENTITY.properties.maxTasks
						.id
			)
		);

		const minTasksProperty = properties.find((property) =>
			property.implements.find(
				(base) =>
					base.id ===
					BUILT_IN_BASE_ENTITY_IDS['built-in-base-entity']
						.BUILT_IN_CONTAINER_COMPUTE_ENTITY.properties.minTasks
						.id
			)
		);

		const maxContainers = maxTasksProperty?.defaultValue?.value as number;
		const minContainers = minTasksProperty?.defaultValue?.value as number;

		const computeSizeProperty = properties.find((property) =>
			property.implements.find(
				(base) =>
					base.id ===
					BUILT_IN_BASE_ENTITY_IDS['built-in-base-entity']
						.BUILT_IN_CONTAINER_COMPUTE_ENTITY.properties
						.computeSize.id
			)
		);
		const computeSize = computeSizeProperty?.defaultValue?.value as string;

		return {
			minContainers,
			maxContainers,
			computeSize
		};
	}, [project?.lastPublishedProjectState, computeId]);

	const mergedConfig = React.useMemo(() => {
		return {
			memory: containerDetails?.memory,
			cpu: containerDetails?.cpu,
			computeSize:
				containerDetails?.computeSize || containerConfig?.computeSize,
			image: containerDetails?.image,
			minInstances:
				containerDetails?.minContainers ??
				containerDetails?.minInstances ??
				containerDetails?.minCapacity ??
				containerDetails?.minCount ??
				containerConfig?.minContainers,
			maxInstances:
				containerDetails?.maxContainers ??
				containerDetails?.maxInstances ??
				containerDetails?.maxCapacity ??
				containerDetails?.maxCount ??
				containerConfig?.maxContainers,
			mountedVolumes: containerDetails?.mountedVolumes || []
		} as any;
	}, [containerDetails, containerConfig]);

	const fetchTasks = async () => {
		setIsLoading(true);
		try {
			const accessToken = await getAccessToken();
			const computeBaseUrl = envConfig.API_BASE_URL.replace(
				'//api.',
				'//compute.'
			);
			const response = await axios.get(
				`${computeBaseUrl}/compute-access-service/v1/sessions/tasks?projectId=${projectId}&entityId=${computeId}&envIdentifier=${envIdentifier}${tenantAccountId ? `&awsAccountId=${tenantAccountId}` : ''}`,
				{
					headers: { Authorization: `Bearer ${accessToken}` }
				}
			);

			const fetchedTasks = (response.data?.tasks || []) as {
				privateIp: string;
				publicIp: string;
				status: 'RUNNING' | 'STOPPED' | 'PROVISIONING' | 'PENDING';
				taskArn: string;
				stoppedReason?: string;
			}[];
			setTasks(fetchedTasks);

			if (fetchedTasks.length > 0) {
				if (
					!selectedTask ||
					(typeof selectedTask !== 'string' &&
						(selectedTask.status === 'PROVISIONING' ||
							selectedTask.status === 'PENDING') &&
						fetchedTasks[0].status !== 'PROVISIONING')
				) {
					setSelectedTask(fetchedTasks[0]);
				}
			}
		} catch (error) {
			console.error('Error fetching tasks:', error);
			setTasks([]);
			setSelectedTask(null);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchTasks();
	}, [projectId, computeId]);

	useEffect(() => {
		const isProvisioning = tasks.some(
			(t) => t.status === 'PROVISIONING' || t.status === 'PENDING'
		);

		if (isProvisioning) {
			const timeoutId = window.setTimeout(() => {
				fetchTasks();
			}, 5000);
			return () => window.clearTimeout(timeoutId);
		}
	}, [tasks]);

	const handleBack = () => {
		window.history.back();
	};

	return (
		<div
			style={{
				position: 'relative',
				height: 'calc(100% - 90px)',
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					boxSizing: 'border-box',
					padding: '24px 40px 48px 40px',
					fontFamily: "'Outfit', 'Inter', sans-serif"
				}}
			>
				{/* Top Panel */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: '20px'
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px'
						}}
					>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<button
								onClick={handleBack}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									fontSize: '12px',
									fontWeight: 500,
									color: '#6366f1',
									padding: 0,
									alignSelf: 'flex-start',
									transition: 'color 0.15s ease'
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.color = '#4f46e5')
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.color = '#6366f1')
								}
							>
								<i
									className="fa-solid fa-chevron-left"
									style={{ fontSize: '10px' }}
								></i>
								Back to resources
							</button>

							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '10px',
									marginTop: '4px'
								}}
							>
								<img
									src={ContainerComputeIcon}
									alt=""
									style={{
										width: '22px',
										height: '22px',
										objectFit: 'contain'
									}}
								/>
								<h2
									style={{
										margin: '0px',
										fontSize: '18px',
										fontWeight: 600,
										color: '#111827'
									}}
								>
									{headerInfo.name}
								</h2>
							</div>

							<span
								style={{
									fontSize: '12px',
									color: '#6b7280',
									marginTop: '2px'
								}}
							>
								Terminal Access
							</span>
						</div>
					</div>
					<div style={{ display: 'flex', gap: '10px' }}>
						<Button
							variant="outlined"
							size="small"
							iconStart="fa-solid fa-rotate"
							loading={isLoading}
							onClick={() => fetchTasks()}
						>
							Refresh
						</Button>
					</div>
				</div>

				{(containerDetails ||
					Object.keys(containerConfig).length > 0) && (
					<div
						style={{
							display: 'flex',
							gap: '20px',
							marginBottom: '20px',
							padding: '12px 16px',
							backgroundColor: '#f9fafb',
							borderRadius: '8px',
							border: '1px solid #e5e7eb',
							flexWrap: 'wrap'
						}}
					>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								Memory
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: '#374151'
								}}
							>
								{mergedConfig.memory
									? mergedConfig.memory >= 1024
										? `${mergedConfig.memory / 1024} GB`
										: `${mergedConfig.memory} MB`
									: 'N/A'}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								CPU
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: '#374151'
								}}
							>
								{mergedConfig.cpu || 'N/A'}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								Size
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: '#374151'
								}}
							>
								{mergedConfig.computeSize || 'N/A'}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								Image
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: '#374151',
									fontFamily: 'monospace'
								}}
							>
								{mergedConfig.image || 'N/A'}
							</span>
						</div>

						{mergedConfig.minInstances !== undefined && (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '4px'
								}}
							>
								<span
									style={{
										fontSize: '11px',
										color: '#6b7280',
										textTransform: 'uppercase',
										letterSpacing: '0.5px'
									}}
								>
									Min Containers
								</span>
								<span
									style={{
										fontSize: '13px',
										fontWeight: 500,
										color: '#374151'
									}}
								>
									{mergedConfig.minInstances}
								</span>
							</div>
						)}

						{mergedConfig.maxInstances !== undefined && (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '4px'
								}}
							>
								<span
									style={{
										fontSize: '11px',
										color: '#6b7280',
										textTransform: 'uppercase',
										letterSpacing: '0.5px'
									}}
								>
									Max Containers
								</span>
								<span
									style={{
										fontSize: '13px',
										fontWeight: 500,
										color: '#374151'
									}}
								>
									{mergedConfig.maxInstances}
								</span>
							</div>
						)}

						{mergedConfig.mountedVolumes &&
							mergedConfig.mountedVolumes.length > 0 && (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '4px'
									}}
								>
									<span
										style={{
											fontSize: '11px',
											color: '#6b7280',
											textTransform: 'uppercase',
											letterSpacing: '0.5px'
										}}
									>
										Mounted Volumes
									</span>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											gap: '2px'
										}}
									>
										{(() => {
											const groups = new Map<
												string,
												{
													vol: any;
													match: any;
													mountPaths: string[];
												}
											>();
											for (const vol of mergedConfig.mountedVolumes ||
												[]) {
												const match = (
													project?.liveResources || []
												).find(
													(r: any) =>
														r.resourceType ===
															'shared-drive' &&
														r.details
															?.fileSystemId ===
															vol.fileSystemId
												);
												const key =
													vol.fileSystemId ||
													vol.name;
												if (!groups.has(key)) {
													groups.set(key, {
														vol,
														match,
														mountPaths: []
													});
												}
												if (vol.mountPath) {
													groups
														.get(key)!
														.mountPaths.push(
															vol.mountPath
														);
												}
											}
											return Array.from(
												groups.values()
											).map((group, i) => {
												const {
													vol,
													match,
													mountPaths
												} = group;
												const displayName = match
													? match.name
													: vol.fileSystemId;
												return (
													<div
														key={i}
														style={{
															display: 'flex',
															alignItems:
																'center',
															gap: '8px',
															padding: '6px 10px',
															margin: '4px 0',
															borderRadius: '6px',
															backgroundColor:
																match
																	? '#f3f4f6'
																	: 'transparent',
															cursor: match
																? 'pointer'
																: 'default',
															transition:
																'all 0.2s',
															color: match
																? '#111827'
																: '#374151',
															border: match
																? '1px solid #e5e7eb'
																: '1px solid transparent',
															flexWrap: 'wrap'
														}}
														onClick={() => {
															if (match) {
																navigate(
																	`../shared-drive/${match.resourceId}`
																);
															}
														}}
														onMouseEnter={(e) => {
															if (match) {
																e.currentTarget.style.backgroundColor =
																	'#e5e7eb';
																e.currentTarget.style.borderColor =
																	'#d1d5db';
															}
														}}
														onMouseLeave={(e) => {
															if (match) {
																e.currentTarget.style.backgroundColor =
																	'#f3f4f6';
																e.currentTarget.style.borderColor =
																	'#e5e7eb';
															}
														}}
													>
														<div
															style={{
																display: 'flex',
																alignItems:
																	'center',
																gap: '8px'
															}}
														>
															<img
																src={SharedDriveIcon}
																alt=""
																style={{
																	width: '16px',
																	height: '16px',
																	objectFit: 'contain'
																}}
															/>
															<span
																style={{
																	fontSize:
																		'13px',
																	fontWeight:
																		match
																			? 600
																			: 500
																}}
															>
																{displayName}
															</span>
															<span
																style={{
																	color: '#6b7280',
																	fontSize:
																		'11px'
																}}
															>
																({vol.name})
															</span>
														</div>
														<div
															style={{
																display: 'flex',
																gap: '4px',
																flexWrap:
																	'wrap',
																marginLeft:
																	'auto'
															}}
														>
															{mountPaths.map(
																(
																	mp: string,
																	idx: number
																) => (
																	<span
																		key={
																			idx
																		}
																		style={{
																			color: '#4b5563',
																			fontSize:
																				'11px',
																			backgroundColor:
																				match
																					? '#e5e7eb'
																					: '#f3f4f6',
																			padding:
																				'2px 6px',
																			borderRadius:
																				'4px',
																			fontFamily:
																				'monospace'
																		}}
																	>
																		{mp}
																	</span>
																)
															)}
														</div>
													</div>
												);
											});
										})()}
									</div>
								</div>
							)}
					</div>
				)}

				<div
					style={{
						display: 'flex',
						gap: '10px',
						marginBottom: '16px',
						borderBottom: '1px solid #e5e7eb'
					}}
				>
					<button
						onClick={() => setActiveTab('terminal')}
						style={{
							padding: '8px 16px',
							background: 'none',
							border: 'none',
							borderBottom:
								activeTab === 'terminal'
									? '2px solid #4f46e5'
									: '2px solid transparent',
							color:
								activeTab === 'terminal'
									? '#4f46e5'
									: '#6b7280',
							fontWeight: activeTab === 'terminal' ? 600 : 400,
							cursor: 'pointer',
							fontSize: '14px'
						}}
					>
						Terminal
					</button>
					<button
						onClick={() => setActiveTab('logs')}
						style={{
							padding: '8px 16px',
							background: 'none',
							border: 'none',
							borderBottom:
								activeTab === 'logs'
									? '2px solid #4f46e5'
									: '2px solid transparent',
							color: activeTab === 'logs' ? '#4f46e5' : '#6b7280',
							fontWeight: activeTab === 'logs' ? 600 : 400,
							cursor: 'pointer',
							fontSize: '14px'
						}}
					>
						Logs
					</button>
				</div>

				{/* Main Split Layout */}
				<div
					style={{
						display: activeTab === 'terminal' ? 'flex' : 'none',
						flex: 1,
						gap: '20px',
						minHeight: '400px',
						overflow: 'hidden'
					}}
				>
					{/* Left Sidebar: Container/Task Selector */}
					<div
						style={{
							width: '260px',
							display: 'flex',
							flexDirection: 'column',
							border: '1px solid #e0e0e0',
							borderRadius: '10px',
							backgroundColor: '#fdfdfd',
							overflowY: 'auto'
						}}
					>
						<div
							style={{
								padding: '12px 16px',
								borderBottom: '1px solid #e0e0e0',
								fontWeight: 600,
								fontSize: '13px',
								color: '#444',
								backgroundColor: '#f5f5f5'
							}}
						>
							Instances {isLoading ? '' : `(${tasks.length})`}
						</div>
						{isLoading ? (
							<div
								style={{
									padding: '20px',
									textAlign: 'center',
									color: '#666',
									fontSize: '13px'
								}}
							>
								<i
									className="fa-solid fa-spinner fa-spin"
									style={{ marginRight: '8px' }}
								></i>
								Loading instances...
							</div>
						) : tasks.length === 0 ? (
							<div
								style={{
									padding: '20px',
									textAlign: 'center',
									color: '#888',
									fontSize: '13px'
								}}
							>
								No active instances found.
							</div>
						) : (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column'
								}}
							>
								{tasks.map((task: any) => {
									const taskId =
										typeof task === 'string'
											? task
											: task.taskArn
												? task.taskArn.split('/').pop()
												: 'Unknown';
									const isSelected =
										selectedTask === task ||
										(typeof selectedTask === 'object' &&
											(selectedTask as any).taskArn ===
												task.taskArn);

									return (
										<button
											key={taskId}
											onClick={() =>
												setSelectedTask(task)
											}
											style={{
												width: '100%',
												padding: '12px 16px',
												textAlign: 'left',
												background: isSelected
													? 'rgba(99, 102, 241, 0.08)'
													: 'none',
												border: 'none',
												borderBottom:
													'1px solid #f0f0f0',
												cursor: 'pointer',
												display: 'flex',
												flexDirection: 'column',
												gap: '4px',
												transition: 'background 0.2s'
											}}
										>
											<span
												style={{
													fontWeight: isSelected
														? 600
														: 400,
													color: isSelected
														? '#4f46e5'
														: '#333',
													fontSize: '12px',
													wordBreak: 'break-all'
												}}
											>
												{taskId}
											</span>
											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													gap: '2px',
													marginTop: '2px'
												}}
											>
												<span
													style={{
														fontSize: '10px',
														color: '#888'
													}}
												>
													Status:{' '}
													{typeof task === 'string'
														? 'RUNNING'
														: task.status ||
															'RUNNING'}
												</span>
												{task.publicIp && (
													<CopyableIp
														label="Public IP"
														ip={task.publicIp}
													/>
												)}
												{task.privateIp && (
													<CopyableIp
														label="Private IP"
														ip={task.privateIp}
													/>
												)}
											</div>
										</button>
									);
								})}
							</div>
						)}
					</div>

					{/* Right Console: Terminal */}
					<TerminalViewer
						taskId={
							selectedTask
								? typeof selectedTask === 'string'
									? selectedTask
									: (selectedTask as any).taskArn
											?.split('/')
											.pop()
								: null
						}
						projectId={projectId}
						entityId={computeId}
						envIdentifier={envIdentifier}
						tenantAccountId={tenantAccountId}
						stoppedReason={selectedTask?.stoppedReason}
						taskStatus={selectedTask?.status}
					/>
				</div>

				{/* Logs Tab */}
				{activeTab === 'logs' && (
					<div
						style={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden'
						}}
					>
						<CloudLogsViewer
							onClose={onClose}
							project={project}
							projectId={projectId}
							functionName={computeId}
							logGroupName={`/ecs/${toKebabCase(`${projectId}-${computeId}-${envIdentifier}`)}`}
							hideHeader={true}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default ContainerComputeViewer;
